import { html, TemplateResult, CSSResultGroup } from 'lit';
import { customElement, query, property } from 'lit/decorators.js';
import componentStyles from './idblab-header-slider.css.lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import Splide from '@splidejs/splide';

/**
 * The IDB Lab Header Slider
 *
 * @element idblab-header-slider
 * @slot - default slot for any slider content
 */
@customElement('idblab-header-slider')
export class IdblabHeaderSlider extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  /**
   * Label for the Previous button. Keep in mind multilingual support.
   */
  @property({ type: String, reflect: true, attribute: 'previous-label' })
  previousLabel: string;

  @property({ type: Boolean, attribute: 'home-slider' })
  homeSlider = false;

  /**
   * Label for the Next button. Keep in mind multilingual support.
   */
  @property({ type: String, reflect: true, attribute: 'next-label' })
  nextLabel: string;

  @query('.splide')
  splideEl: HTMLElement;

  @query('.splide__arrow--prev')
  prevBtn: HTMLElement;

  @query('.splide__arrow--next')
  nextBtn: HTMLElement;

  splide: Splide;

  observer: MutationObserver;

  connectedCallback() {
    super.connectedCallback();
    this.observer = new MutationObserver(() => {
      this.requestUpdate();
    });
    this.observer.observe(this, { childList: true });
  }

  firstUpdated() {
    const splideTrack = this.shadowRoot?.querySelector(
      '.splide__track'
    ) as HTMLElement;
    const splideList = document.createElement('div');
    splideList.classList.add('splide__list');

    Array.from(this.children).forEach((item, idx, items) => {
      // remove item from lightdom right away to avoid browser repainting while processing
      splideList.appendChild(item);

      // process slide items
      item.classList.add('splide__slide', 'splide__slide__container');
      // item.setAttribute('role', 'group'); // probably it's better to move this to Drupal
      item.setAttribute('aria-label', `slide ${idx + 1} of ${items.length}`); // probably it's better to move this to Drupal
    });

    // append our elements to our shadowdom.

    splideTrack.appendChild(splideList);
    let splideConfig = {};
    if (this.homeSlider) {
      splideConfig = {
        label: 'Header Slider',
        keyboard: true,
        autoplay: true,
        speed: 500,
        reducedMotion: true,
        arrows: true,
        height: '100%',
        padding: { left: '0', right: '0' },
        perPage: 1,
        focusableNodes:
          'a, button, textarea, input, select, iframe, idb-button, idb-button a, idb-heading a',
        rewindByDrag: true,
        drag: true,
        updateOnMove: true,
        snap: true,
        type: 'loop',
        gap: '2rem',
        breakpoints: {
          1024: {
            pagination: true,
            drag: true,
            updateOnMove: true,
          },
          768: {
            pagination: true,
            drag: true,
            updateOnMove: true,
          },
          640: {
            pagination: true,
            arrows: false,
            rewindByDrag: true,
            drag: true,
            updateOnMove: true,
          },
        },
      };
    } else {
      splideConfig = {
        label: 'Header Slider',
        keyboard: true,
        autoplay: true,
        speed: 500,
        reducedMotion: true,
        arrows: true,
        height: '100%',
        // padding: { left: '12%', right: '12%' },
        perPage: 1,
        focusableNodes:
          'a, button, textarea, input, select, iframe, idb-button, idb-button a, idb-heading a',
        rewindByDrag: true,
        drag: true,
        updateOnMove: true,
        snap: true,
        type: 'loop',
        gap: '2rem',
        breakpoints: {
          1024: {
            pagination: true,
            // padding: { left: '20%', right: '20%' },
            drag: true,
            updateOnMove: true,
          },
          768: {
            pagination: true,
            // padding: { left: '10%', right: '10%' },
            drag: true,
            updateOnMove: true,
          },
          640: {
            // padding: { left: '2%', right: '2%' },
            pagination: true,
            arrows: false,
            rewindByDrag: true,
            drag: true,
            updateOnMove: true,
          },
        },
      };
    }

    this.splide = new Splide(this.splideEl, splideConfig).mount();

    // set aria-label of buttons for screen readers
    if (this.previousLabel) {
      this.prevBtn.setAttribute('aria-label', this.previousLabel);
    }
    if (this.nextLabel) {
      this.nextBtn.setAttribute('aria-label', this.nextLabel);
    }
  }

  updated() {
    setTimeout(() => {
      this.setHiddenAttrOnSlides();
    }, 1000);
  }

  disconnectedCallback() {
    this.observer.disconnect();
  }

  render(): TemplateResult {
    return html`
      <div class="splide">
        <div class="splide__track"></div>
          <div class="splide__arrows splide__arrows--ltr">
            <button
              class="splide__arrow splide__arrow--prev"
              type="button"
              aria-label="Previous slide"
              aria-controls="splide01-track"
            >
              <svg width="40" height="40" viewBox="0 0 40 40"   fill="none" xmlns="http://www.w3.org/2000/svg">
                <path class="circle" d="M0 20C0 8.95431 8.95431 0 20  0C31.0457 0 40 8.95431 40 20C40 31.0457 31.0457 40 20  40C8.95431 40 0 31.0457 0 20Z" />
                <path d="M27 20H13" stroke="white" stroke-width="2"   stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M20 27L13 20L20 13" stroke="white"   stroke-width="2" stroke-linecap="round"   stroke-linejoin="round"/>
              </svg>
            </button>
            <button
              class="splide__arrow splide__arrow--next"
              type="button"
              aria-label="Next slide"
              aria-controls="splide01-track"
            >
              <svg width="40" height="40" viewBox="0 0 40 40"   fill="none" xmlns="http://www.w3.org/2000/svg">
                <path class="circle" d="M0 20C0 8.95431 8.95431 0 20  0C31.0457 0 40 8.95431 40 20C40 31.0457 31.0457 40 20  40C8.95431 40 0 31.0457 0 20Z" fill="#A6A6A8"/>
                <path d="M13 20H27" stroke="white" stroke-width="2"   stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M20 13L27 20L20 27" stroke="white"   stroke-width="2" stroke-linecap="round"   stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    `;
  }

  setHiddenAttrOnSlides = () => {
    const hiddenSlides: NodeListOf<HTMLElement> | undefined =
      this.shadowRoot?.querySelectorAll('.splide__slide[aria-hidden="true"]');

    // Sets the not-visible attribute/prop on the card when hidden.
    // On the card component, the tabindex value is controlled by this.
    if (hiddenSlides?.length) {
      Array.from(hiddenSlides).map(e => {
        e.querySelectorAll('a').forEach(e => e.setAttribute('tabindex', '-1'));
      });
    }
  };
}
